import { useState, useEffect, useMemo } from "react";
import { SubtleButton } from "components/button";
import { useModalContext } from "components/modal";
import styled from "styled-components";
import { theme } from "styles/theme";
import { TTargetType } from "types/target-type";
import { TTargetTax } from "types/tax";
import { dollarFormatter } from "utils";
import { useTranslation } from "react-i18next";
import { calculateAdjustedTaxableTotal, reorderTaxes } from "../../utils";
import { IoAddCircleOutline, IoCloseCircleOutline } from "react-icons/io5";
import { useSnackbarContext } from "components/snackbar";
import axios from "axios";
import Select from "react-select";
import TaxTypeArea from "./TaxTypeArea";
import { useLineItemContext } from "../../context/LineItemContext";

type Props = {
  view: "read" | "edit" | "edit-taxes";
  total: { list: number; sale: number; install: number };
  taxableTotal: { list: number; sale: number; install: number };
  id: string;
  type: TTargetType;
  priceType: "list" | "install" | "sale";
};

export default function Taxes(props: Props) {
  const { t } = useTranslation();
  const tBase = "containers.lineItems.taxes";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { showSnackbar } = useSnackbarContext();
  const { showMath, setShowMath, setTaxableProductTotal, taxes, setTaxes, hasExistingTaxes } = useLineItemContext();

  const [showGroupSelect, setShowGroupSelect] = useState(false);

  useEffect(() => {
    (async () => {
      if (taxGroupsRaw.length === 0) {
        const groups = await fetchTaxGroups();
        setTaxGroupsRaw(groups);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalizeGroups = (groups: any[]) =>
    (groups || []).map(g => ({
      ...g,
      taxes: (g.taxes || []).filter((t: any) => t && t.id != null)
    }));
  
  const isCombinedGroup = (g: any) =>
    Array.isArray(g.taxes) &&
    g.taxes.length > 0 &&
    g.taxes.every((t: any) => t.tax_type === "tax");
  
  const isRegularGroup = (g: any) =>
    Array.isArray(g.taxes) &&
    g.taxes.length > 0 &&
    !isCombinedGroup(g);

  const [taxGroupsRaw, setTaxGroupsRaw] = useState<any[]>([]);
  const taxGroups = useMemo(() => normalizeGroups(taxGroupsRaw), [taxGroupsRaw]);

  const isEdit = props.view === "edit" || props.view === "edit-taxes";

  const fetchTaxGroups = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/tax_groups`);
      return res.data;
    } catch (error) {
      console.error('Error fetching tax groups:', error);
      return [];
    }
  };

  const groupedTaxes = taxes ? [...taxes].reduce((acc, tax) => {
    const type = tax.tax_type;
    if (typeof type === "string") {
      if (!acc[type]) acc[type] = [];
      acc[type].push(tax);
    }
    return acc;
  }, {} as Record<string, TTargetTax[]>) : {};

  // Sort each group by ordering
  Object.keys(groupedTaxes).forEach(type => {
    groupedTaxes[type].sort((a, b) => (a.ordering || 0) - (b.ordering || 0));
  });

  const orderedTaxes = taxes ? [...taxes].sort((a, b) => {
    return (a.ordering || 0) - (b.ordering || 0);
  }) : [];

  const addTaxGroup = (selectedGroup: any) => {
    if (!selectedGroup.taxes || selectedGroup.taxes.length === 0) {
      showSnackbar(`No items found in group "${selectedGroup.label}"`);
      setShowGroupSelect(false);
      return;
    }

    // Separate taxes from other items in the group
    const groupTaxes = selectedGroup.taxes.filter((item: any) => item.tax_type === "tax");
    const groupNonTaxes = selectedGroup.taxes.filter((item: any) => item.tax_type !== "tax");

    // Remove any existing taxes (since user can only have one tax)
    const taxesWithoutTaxes = taxes.filter(tax => tax.tax_type !== "tax");
    
    let updatedTaxes = [...taxesWithoutTaxes];

    // Add ALL taxes from group (not just the first one)
    if (groupTaxes.length > 0) {
      const newTaxes: TTargetTax[] = groupTaxes.map((tax: any) => ({
        ...tax,
        tax_id: tax.id,
        id: tax.id,
        tax_type: "tax",
        list_rate:    tax.list_rate    || 0,
        sale_rate:    tax.sale_rate    || tax.list_rate || 0,
        install_rate: tax.install_rate || tax.list_rate || 0,
        list_type:    tax.list_type    || "percent",
        sale_type:    tax.sale_type    || tax.list_type || "percent",
        install_type: tax.install_type || tax.list_type || "percent",
      }));
      updatedTaxes.push(...newTaxes);
    }

    // Filter out non-tax items that already exist to prevent duplicates
    const existingNonTaxIds = new Set(
      taxesWithoutTaxes.map(tax => String(tax.tax_id ?? tax.id))
    );
    
    const uniqueNonTaxes = groupNonTaxes.filter(
      (item: any) => !existingNonTaxIds.has(String(item.id))
    );

    // Add unique non-tax items from group (fees, discounts)
    const newNonTaxes: TTargetTax[] = uniqueNonTaxes.map((item: any) => ({
      ...item,
      tax_id: item.id,
      id: item.id,
      tax_type: item.tax_type,
      list_rate:    item.list_rate    || 0,
      sale_rate:    item.sale_rate    || item.list_rate || 0,
      install_rate: item.install_rate || item.list_rate || 0,
      list_type:    item.list_type    || "percent",
      sale_type:    item.sale_type    || item.list_type || "percent",
      install_type: item.install_type || item.list_type || "percent",
    }));
    updatedTaxes.push(...newNonTaxes);

    // Apply reordering and update
    const finalTaxes = reorderTaxes(updatedTaxes);
    setTaxes(finalTaxes);
    setShowGroupSelect(false);

    const taxCount = groupTaxes.length;
    const addedNonTaxCount = uniqueNonTaxes.length;
    const skippedNonTaxCount = groupNonTaxes.length - addedNonTaxCount;
    
    if (taxCount > 0 && addedNonTaxCount > 0) {
      let message = `Added ${taxCount} taxes and ${addedNonTaxCount} other items from "${selectedGroup.label}"`;
      if (skippedNonTaxCount > 0) {
        message += ` (${skippedNonTaxCount} already present)`;
      }
      showSnackbar(message);
    } else if (taxCount > 0) {
      let message = `Added ${taxCount} taxes from "${selectedGroup.label}"`;
      if (skippedNonTaxCount > 0) {
        message += ` (${skippedNonTaxCount} other items already present)`;
      }
      showSnackbar(message);
    } else if (addedNonTaxCount > 0) {
      let message = `Added ${addedNonTaxCount} items from "${selectedGroup.label}"`;
      if (skippedNonTaxCount > 0) {
        message += ` (${skippedNonTaxCount} already present)`;
      }
      showSnackbar(message);
    } else if (skippedNonTaxCount > 0) {
      showSnackbar(`All items from "${selectedGroup.label}" are already present`);
    }
  };
  
  const adjustedTaxableTotal = calculateAdjustedTaxableTotal(taxes, props.total, props.taxableTotal);
  const subtotal = props.total[props.priceType];
  const adjustedTotal = adjustedTaxableTotal[props.priceType];
  const subAndFeeDiscount = adjustedTaxableTotal.allProductsWithFeesDiscounts[props.priceType];

  useEffect(() => {
    setTaxableProductTotal(subAndFeeDiscount);
  }, [subAndFeeDiscount, setTaxableProductTotal]);

  return (
    <Con>
      <div className="flex items-center justify-between py-2">
        <div>
          <div>{tr("Subtotal")}</div>
          <div className="text-gray-500">Taxable Subtotal</div>
        </div>
        <div>
          <div className={`${showMath ? "text-secondary" : "text-gray-500"}`}>{dollarFormatter(subtotal)}</div>
          <div className={`${showMath ? "text-primary" : "text-gray-500"}`}>{dollarFormatter(adjustedTotal)}</div>
        </div>
      </div>
  
      {/* Tax Groups */}
      {["discount", "fee", "tax"].map((type) => (
        <TaxTypeArea
          key={type}
          taxes={groupedTaxes[type] || []}
          groupType={type}
          isEdit={isEdit}
          priceType={props.priceType}
          total={props.total}
          adjustedTaxableTotal={adjustedTaxableTotal}
          orderedTaxes={orderedTaxes}
          allTaxes={taxes}
          addTaxGroup={addTaxGroup}
          fetchTaxGroups={fetchTaxGroups}
          taxGroups={taxGroups}
          setTaxGroups={setTaxGroupsRaw}
          isCombinedGroup={isCombinedGroup}
        />
      ))}

      <div className="flex items-center justify-between">
        <div className="flex items-center justify-between flex-1">
          {isEdit && (
            showGroupSelect ? (
              <div className="flex items-center space-x-2 flex-1">
                <Select
                  className="flex-[0.5_0.5_20%]"
                  options={taxGroups
                    .filter(isRegularGroup)
                    .map(group => ({ label: group.label, value: group.id, ...group }))}
                  placeholder="Select a tax group"
                  onChange={(selectedOption: any) => {
                    if (selectedOption) {
                      addTaxGroup(selectedOption);
                    }
                  }}
                  onMenuOpen={async () => {
                    if (taxGroups.length === 0) {
                      const groups = await fetchTaxGroups();
                      setTaxGroupsRaw(groups);
                    }
                  }}
                />
                <button 
                  onClick={() => setShowGroupSelect(false)}
                  className="text-red-500 hover:text-red-700 transition-colors"
                >
                  <IoCloseCircleOutline size={20} />
                </button>
              </div>
            ) : (
              <SubtleButton
                primary
                onClick={() => setShowGroupSelect(true)}
              >
                Add Group
              </SubtleButton>
            )
          )}
        </div>
        <div>
          <button 
            className="text-[13.6px] py-[13.6px] text-secondary hover:text-secondary-dark"
            onClick={() => setShowMath && setShowMath(!showMath)}
          >
              {showMath ? "Hide Math" : tr("Show Math")}
            </button>
        </div>
      </div>
    </Con>
  );
}

const Con = styled.div`
  align-self: flex-end;
  width: 100%;
  max-width: 500px;
`;
